import { TFunction } from 'i18next';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { DateCell, DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import {
    DataPopulation,
    PanelTimePeriod,
    SiteAverage,
    StableVariableType,
    ThresholdCalculation,
} from 'types/graphql-global-types';

const { TooltipCell } = useGridCustomCell();

export const commonVisibleColumns = (tCommon: TFunction, panel: string): GridColumnProps[] => {
    return [
        {
            field: 'active',
            title: tCommon('panel-active'),
            id: 'panel-active',
            minWidth: 95,
            cells: { data: YesNoCell },
            filter: 'boolean',
            lockedByDefault: true,
        },
        {
            field: 'name',
            title: tCommon('panel-plotTitle'),
            id: 'panel-plotTitle',
            minWidth: 200,
            filter: 'text',
            lockedByDefault: true,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: `${panel}.xAxisTitle`,
            title: tCommon('panel-xTitle'),
            id: 'panel-xTitle',
            filter: 'text',
            minWidth: 200,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: `${panel}.yAxisTitle`,
            title: tCommon('panel-yTitle'),
            id: 'panel-yTitle',
            filter: 'text',
            minWidth: 200,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
    ];
};
export const commonCreatedUpdatedColumns = (tCommon: TFunction): GridColumnProps[] => {
    return [
        {
            title: tCommon('panel-createdBy'),
            field: 'createdByFullName',
            id: 'panel-createdBy',
            minWidth: 180,
            filter: 'text',
        },
        {
            title: tCommon('panel-created'),
            field: 'created',
            id: 'panel-created',
            filter: 'date',
            minWidth: 145,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
        },
        {
            title: tCommon('panel-updatedBy'),
            field: 'updatedByFullName',
            id: 'panel-updatedBy',
            minWidth: 180,
            filter: 'text',
        },
        {
            title: tCommon('panel-updated'),
            field: 'updated',
            id: 'panel-updated',
            filter: 'date',
            minWidth: 145,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
        },
    ];
};
export const commonHiddenColumns = (tCommon: TFunction): GridColumnProps[] => {
    return [
        {
            title: tCommon('panel-configId'),
            field: 'configId',
            id: 'panel-configId',
            minWidth: 180,
            filter: 'text',
            showByDefault: false,
        },
        {
            title: tCommon('panel-recordId'),
            field: 'recordId',
            id: 'panel-recordId',
            minWidth: 80,
            filter: 'numeric',
            showByDefault: false,
        },
    ];
};

export const TriOpDateColumns = (tCommon: TFunction, t: TFunction) => {
    const columns: GridColumnProps[] = [
        ...commonVisibleColumns(tCommon, 'triOpDateParameters'),
        {
            field: 'weight',
            title: t('panel-weight'),
            id: 'panel-weight',
            minWidth: 70,
            filter: 'numeric',
        },
        {
            field: 'includeInSiteScoring',
            title: t('panel-includeInSiteScoring'),
            id: 'panel-includeInSiteScoring',
            minWidth: 120,
            cells: { data: YesNoCell },
            filter: 'boolean',
        },
        {
            field: 'decision',
            title: t('panel-decision'),
            id: 'panel-decision',
            cells: { data: YesNoCell },
            filter: 'boolean',
            minWidth: 70,
        },
        {
            field: 'triOpDateParameters.minDataNeeded',
            title: t('panel-minDataNeeded'),
            id: 'panel-minDataNeeded',
            minWidth: 100,
            filter: 'numeric',
        },
        {
            field: 'rowData',
            title: t('panel-RowData'),
            id: 'panel-RowData',
            filter: 'text',
            minWidth: 210,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'yAxisData',
            title: t('panel-YAxisDataField1'),
            id: 'panel-YAxisDataField1',
            filter: 'text',
            minWidth: 210,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'sortField',
            title: t('panel-sortField'),
            id: 'panel-sortField',
            filter: 'text',
            minWidth: 200,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'siteAverage',
            title: t('panel-siteAverage'),
            id: 'panel-siteAverage',
            filter: 'text',
            minWidth: 150,
            lovData: [t(SiteAverage.MEAN), t(SiteAverage.MEDIAN)],
        },
        {
            field: 'dataPopulation',
            title: t('panel-dataPopulation'),
            id: 'panel-dataPopulation',
            filter: 'text',
            minWidth: 150,
            lovData: [t(DataPopulation.ALL), t(DataPopulation.MOST_RECENT)],
        },
        {
            title: t('panel-thresholds'),
            id: 'panel-thresholds',
            field: 'field1',
            children: [
                {
                    field: 'thresholdCalculation',
                    title: t('panel-thresholdCalculation'),
                    minWidth: 170,
                    id: 'panel-thresholdCalculation',
                    filter: 'text',
                    lovData: [
                        t(ThresholdCalculation.MEAN_SD),
                        t(ThresholdCalculation.POISSON),
                        t(ThresholdCalculation.STUDY_SPECIFIC),
                    ],
                },
                {
                    field: 'triOpDateParameters.amberThreshold',
                    title: t('panel-amberThreshold'),
                    minWidth: 100,
                    filter: 'numeric',
                    id: 'panel-amberThreshold',
                },
                {
                    field: 'triOpDateParameters.redThreshold',
                    title: t('panel-redThreshold'),
                    minWidth: 100,
                    filter: 'numeric',
                    id: 'panel-redThreshold',
                },
                {
                    field: 'triOpDateParameters.bigRedThreshold',
                    title: t('panel-bigRedThreshold'),
                    minWidth: 100,
                    filter: 'numeric',
                    id: 'panel-bigRedThreshold',
                },
            ],
        },
        {
            title: t('panel-dataInclusion'),
            id: 'panel-dataInclusion',
            field: 'field2',
            children: [
                {
                    field: 'dataInclusionUnit',
                    title: t('panel-dataInclusionUnit'),
                    minWidth: 100,
                    filter: 'numeric',
                    id: 'panel-dataInclusionUnit',
                    lovData: Array.from(Array(30).keys()).map((item) => `${item + 1}`),
                },
                {
                    field: 'dataInclusionPeriod',
                    title: t('panel-dataInclusionPeriod'),
                    minWidth: 130,
                    filter: 'text',
                    id: 'panel-dataInclusionPeriod',
                    lovData: [
                        t(PanelTimePeriod.DAYS),
                        t(PanelTimePeriod.WEEKS),
                        t(PanelTimePeriod.MONTHS),
                        t(PanelTimePeriod.QUARTERS),
                        t(PanelTimePeriod.YEARS),
                    ],
                },
            ],
        },
        {
            title: t('panel-stable'),
            id: 'panel-stable',
            field: 'field3',
            children: [
                {
                    field: 'stableVariableType',
                    title: t('panel-stableVariableType'),
                    minWidth: 140,
                    filter: 'text',
                    id: 'panel-stableVariableType',
                    lovData: [t(StableVariableType.NUMBER), t(StableVariableType.PERCENTAGE)],
                },
                {
                    field: 'triOpDateParameters.stableValue',
                    title: t('panel-stableValue'),
                    minWidth: 100,
                    filter: 'numeric',
                    id: 'panel-stableValue',
                },
            ],
        },
        {
            field: 'triOpDateParameters.greenMaxScore',
            title: t('panel-greenMaxScore'),
            id: 'panel-greenMaxScore',
            filter: 'numeric',
            minWidth: 85,
        },
        {
            field: 'triOpDateParameters.amberMaxScore',
            title: t('panel-amberMaxScore'),
            id: 'panel-amberMaxScore',
            filter: 'numeric',
            minWidth: 85,
        },
        {
            field: 'triOpDateParameters.yAxisColumnValue',
            title: t('panel-yAxisColumnValue'),
            id: 'panel-yAxisColumnValue',
            filter: 'text',
            minWidth: 150,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        ...commonCreatedUpdatedColumns(tCommon),
        ...commonHiddenColumns(tCommon),
    ];

    return columns;
};

export const TEMP_COLUMNS: GridColumnProps[] = [
    {
        field: 'active',
        title: 'Active',
        id: 'panel-active',
        minWidth: 95,
        cells: { data: YesNoCell },
        filter: 'boolean',
        lockedByDefault: true,
    },
    {
        field: 'name',
        title: 'Panel Name',
        id: 'panel-panelName',
        minWidth: 180,
        lockedByDefault: true,
    },
    {
        field: 'panelTypeLabel',
        title: 'Panel Type',
        id: 'panel-panelType',
        minWidth: 180,
    },
    {
        field: 'model',
        title: 'Model',
        id: 'panel-model',
        minWidth: 140,
    },
    {
        field: 'plotTitle',
        title: 'Plot Title',
        id: 'panel-plotTitle',
        minWidth: 180,
    },
    {
        field: 'xAxisLabel',
        title: 'X-Axis Label',
        id: 'panel-xTitle',
        minWidth: 180,
    },
    {
        field: 'yAxisLabel',
        title: 'Y-Axis Label',
        id: 'panel-yTitle',
        minWidth: 180,
    },
    {
        field: 'weight',
        title: 'Weight',
        id: 'panel-weight',
        minWidth: 140,
        filter: 'numeric',
    },
    {
        field: 'decision',
        title: 'Decision',
        id: 'panel-decision',
        cells: { data: YesNoCell },
        filter: 'boolean',
        minWidth: 140,
    },
    {
        field: 'includeInSiteScoring',
        title: 'Include in Scoring',
        id: 'panel-includeInSiteScoring',
        minWidth: 210,
        cells: { data: YesNoCell },
        filter: 'boolean',
    },
    {
        title: 'Created By',
        field: '_createdByString',
        id: 'panel-createdby',
        minWidth: 180,
    },
    {
        title: 'Created Date',
        field: 'created',
        id: 'panel-created',
        filter: 'date',
        minWidth: 180,
        cells: { data: DateTimeCell('DD-MMM-yyyy') },
        cellName: 'DateCell',
    },
    {
        title: 'Updated By',
        field: '_updatedByString',
        id: 'panel-updatedby',
        minWidth: 180,
    },
    {
        title: 'Updated Date',
        field: 'updated',
        id: 'panel-updated',
        filter: 'date',
        minWidth: 190,
        cells: { data: DateTimeCell('DD-MMM-yyyy') },
        cellName: 'DateCell',
    },
];
